import { wrapLabel } from "./utils";

/**
 * Bề ngang tối thiểu dành cho MỘT nhãn hạng mục trên trục X, tính bằng px.
 *
 * Đủ cho ~14 ký tự ở cỡ chữ 11px, tức một dòng của `wrapLabel`. Biểu đồ nhân số
 * này với số hạng mục để ra bề ngang tối thiểu của chính nó, rồi cho container
 * cuộn ngang — chữ nằm ngang mà không phải bóp vùng vẽ lại.
 */
export const CATEGORY_TICK_WIDTH = 110;

/** Số dòng tối đa của một nhãn, và chiều cao trục X cần để chứa chúng. */
const MAX_TICK_LINES = 3;
const TICK_LINE_HEIGHT = 13;
export const CATEGORY_AXIS_HEIGHT = MAX_TICK_LINES * TICK_LINE_HEIGHT + 12;

/**
 * Số px phải CỘNG THÊM vào chiều cao biểu đồ để bù cho trục X cao hơn.
 *
 * Recharts lấy chiều cao trục ra khỏi tổng chiều cao truyền vào, nên nhãn ba
 * dòng sẽ âm thầm làm vùng vẽ thấp đi đúng phần trục cao thêm. Cộng lại ở nơi
 * gọi để nhãn dài không phải trả giá bằng biểu đồ nhỏ hơn.
 *
 * 30 là chiều cao trục X mặc định của recharts — mốc mà chiều cao cũ đã trừ.
 */
export const CATEGORY_AXIS_EXTRA_HEIGHT = CATEGORY_AXIS_HEIGHT - 30;

/**
 * Value label printed on top of every bar. Pass as `<Bar label={BAR_VALUE_LABEL}>`.
 *
 * One shared object rather than a per-chart literal: 40 bars across 7 tabs would
 * otherwise drift apart in size, colour and number format one edit at a time.
 *
 * A zero renders as nothing. A "0" floating above a bar of no height reads as a
 * stray mark, and on the filtered views (chưa đóng học phí, nữ, …) most of the
 * chart can be zero — the labels would be noise in exactly the places where the
 * empty bars already say it.
 *
 * The number is grouped vi-VN ("1.234"), matching every other figure on the page.
 */
export const BAR_VALUE_LABEL = {
	position: "top",
	fontSize: 11,
	// Token, not a hex: an axis label is muted-foreground by role, and a hex
	// cannot follow the .dark class the way the CSS variable does.
	className: "fill-muted-foreground",
	formatter: (value: unknown) =>
		typeof value === "number" && value > 0 ? value.toLocaleString("vi-VN") : "",
} as const;

/**
 * Turns off recharts' grow-from-zero animation. Spread onto every `<Bar>` and
 * `<Pie>`: `<Bar {...NO_ANIMATION} …>`.
 *
 * These charts are re-rendered by the live event stream, not only by navigation.
 * Animating a re-render replays every bar from the axis, which reads as the page
 * having reloaded — the exact thing the in-place refresh exists to avoid. The
 * animation only ever paid for itself on first paint, and it costs correctness of
 * perception on every refresh after that.
 *
 * `donut-breakdown` already sets `isAnimationActive={false}` inline for the same
 * reason and predates this constant.
 */
export const NO_ANIMATION = { isAnimationActive: false } as const;

interface TickProps {
	x?: number;
	y?: number;
	payload?: { value?: string | number };
}

/**
 * Nhãn trục X nhiều dòng, chữ NẰM NGANG.
 *
 * Recharts chỉ cho hai lựa chọn sẵn: một dòng bị cắt, hoặc `angle` xoay chữ.
 * Cả hai đều không dùng được với tên ngành đầy đủ, nên trục nhận component này
 * qua prop `tick`. Mỗi dòng là một `<tspan>` — SVG không tự ngắt dòng như HTML.
 *
 * `textAnchor="middle"` canh nhãn vào giữa cột của nó; đây là lý do biểu đồ phải
 * có đủ bề ngang cho mỗi hạng mục (xem `CATEGORY_TICK_WIDTH`), nếu không các
 * nhãn canh giữa sẽ chồng lên nhau.
 */
export function WrappedCategoryTick({ x = 0, y = 0, payload }: TickProps) {
	const lines = wrapLabel(String(payload?.value ?? ""), 14, MAX_TICK_LINES);

	return (
		<g transform={`translate(${x},${y})`}>
			<text textAnchor="middle" className="fill-muted-foreground" fontSize={11}>
				{lines.map((line, index) => (
					<tspan key={index} x={0} dy={index === 0 ? 14 : TICK_LINE_HEIGHT}>
						{line}
					</tspan>
				))}
			</text>
		</g>
	);
}

/* ------------------------------------------------------------------ *
 * Biểu đồ thanh NGANG (recharts `layout="vertical"`)
 *
 * Khi hạng mục là tên người/tên trường, thanh ngang đọc dễ hơn cột dọc:
 * nhãn chạy cùng chiều với chữ, không cần cuộn ngang, và thêm một hạng
 * mục chỉ tốn thêm một hàng thay vì bóp hẹp mọi cột.
 * ------------------------------------------------------------------ */

/** Số ký tự và số dòng của một nhãn trên trục dọc. */
const Y_TICK_MAX_CHARS = 18;
const Y_TICK_MAX_LINES = 2;

/**
 * Bề ngang trục dọc, đủ cho `Y_TICK_MAX_CHARS` ký tự ở cỡ chữ 11px cộng
 * khoảng thở tới thanh. Recharts không tự đo chữ nên phải nói trước.
 */
export const CATEGORY_Y_AXIS_WIDTH = 130;

/** Chiều cao dành cho MỘT hàng, đủ cho hai thanh nhóm cạnh nhau. */
const CATEGORY_ROW_HEIGHT = 44;

/**
 * Chiều cao biểu đồ cho `count` hạng mục.
 *
 * Biểu đồ ngang cao theo số hạng mục chứ không cố định: chiều cao cố định
 * khiến 15 người bị nén thành các vạch mỏng, còn 3 người thì thành các
 * dải dày bất thường. Sàn 240px giữ cho biểu đồ ít mục vẫn ra hình.
 */
export function chartHeightForRows(count: number): number {
	return Math.max(240, Math.max(count, 1) * CATEGORY_ROW_HEIGHT + 40);
}

/**
 * Nhãn trục DỌC nhiều dòng, chữ nằm ngang, canh phải về phía thanh.
 *
 * Cùng lý do tồn tại như `WrappedCategoryTick` — recharts chỉ cho một dòng
 * bị cắt hoặc xoay chữ — nhưng canh khác: `textAnchor="end"` để mọi nhãn
 * kết thúc thẳng hàng sát trục, và khối chữ được dời lên nửa chiều cao của
 * chính nó để tâm nhãn trùng tâm thanh.
 */
export function WrappedCategoryTickY({ x = 0, y = 0, payload }: TickProps) {
	const lines = wrapLabel(String(payload?.value ?? ""), Y_TICK_MAX_CHARS, Y_TICK_MAX_LINES);
	const offsetY = -((lines.length - 1) * TICK_LINE_HEIGHT) / 2 + 4;

	return (
		<g transform={`translate(${x},${y})`}>
			<text textAnchor="end" className="fill-muted-foreground" fontSize={11}>
				{lines.map((line, index) => (
					<tspan key={index} x={-8} dy={index === 0 ? offsetY : TICK_LINE_HEIGHT}>
						{line}
					</tspan>
				))}
			</text>
		</g>
	);
}

/**
 * `BAR_VALUE_LABEL` cho thanh ngang: số nằm ở đầu thanh, bên phải.
 *
 * "top" của một thanh ngang là cạnh trên của chính nó, nên dùng lại hằng
 * kia sẽ đặt số đè lên thanh.
 */
export const BAR_VALUE_LABEL_RIGHT = {
	...BAR_VALUE_LABEL,
	position: "right",
} as const;

/**
 * Bề ngang tối thiểu để `count` hạng mục vừa đủ chỗ cho nhãn của mình.
 *
 * Trả về chuỗi CSS đặt vào `min-width` của phần tử bọc biểu đồ. Dưới ngưỡng đó
 * biểu đồ vẫn giãn hết khung (`100%`); trên ngưỡng thì container cuộn ngang.
 */
export function chartMinWidth(count: number): string {
	return `${Math.max(count, 1) * CATEGORY_TICK_WIDTH}px`;
}
